import { Boxes } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import paths from '@/lib/paths';
import useLocalSettings from '@/stores/useLocalSettings';

import type { PolicyPackage } from '../../types';
import { dueDateToReadable, formatPremiumWithInterval } from '../../utils';

type Props = {
  policyPackage: PolicyPackage;
};

export default function PolicyPackageItem({ policyPackage }: Props) {
  const { t } = useTranslation();

  const { onlyShowRelevant } = useLocalSettings();

  const {
    analysis: {
      relevant: { policyIds },
    },
  } = useEmail();

  const policies = onlyShowRelevant
    ? policyPackage.insurance_policies.filter((p) => policyIds?.includes(p.id))
    : policyPackage.insurance_policies;

  const policyName = policies.length === 1 ? policies[0]?.name : undefined;

  return (
    <Link
      to={generatePath(paths.policyPackages.detail, { id: policyPackage.id })}
      className="shadow-card flex flex-col gap-2 rounded-lg border p-2"
    >
      <div className="flex flex-col text-sm">
        <div className="flex items-center gap-1">
          <Boxes className="size-3.5" strokeWidth={1.5} />

          {policyName && (
            <span className="leading-tight">
              {policyName} ({policyPackage.name})
            </span>
          )}
          {!policyName && (
            <span className="leading-tight">{policyPackage.name}</span>
          )}
        </div>

        <span className="text-muted-foreground text-xs">
          {policyPackage.package_nr}
        </span>
      </div>

      <div className="grid grid-cols-2">
        <div className="flex flex-col">
          <span className="text-muted-foreground text-[10px] uppercase">
            {t('Vervaldatum')}
          </span>

          <span className="text-sm leading-tight">
            {dueDateToReadable(policyPackage.due_date) ?? '-'}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="text-muted-foreground text-[10px] uppercase">
            {t('Premie per termijn')}
          </span>

          <span className="text-sm leading-tight">
            {formatPremiumWithInterval(
              policyPackage.premium,
              policyPackage.payment_interval,
              t,
            ) ?? '-'}
          </span>
        </div>
      </div>
    </Link>
  );
}
